import { useState } from 'react'
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip
} from '@mui/material'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CheckIcon from '@mui/icons-material/Check'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { useDC, DistributionCenter } from '../../contexts/DCContext'

export default function DCSelector() {
  const { distributionCenters, selectedDC, setSelectedDC, isLoading } = useDC()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (dc: DistributionCenter) => {
    setSelectedDC(dc)
    handleClose()
  }

  if (isLoading) {
    return (
      <Chip
        icon={<WarehouseIcon />}
        label="Loading..."
        variant="outlined"
        size="small"
      />
    )
  }

  if (!selectedDC) {
    return (
      <Chip
        icon={<WarehouseIcon />}
        label="No DC"
        variant="outlined"
        color="error"
        size="small"
      />
    )
  }

  return (
    <Box>
      <Button
        onClick={handleClick}
        startIcon={<WarehouseIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          color: 'text.primary',
          backgroundColor: 'grey.100',
          '&:hover': {
            backgroundColor: 'grey.200'
          },
          textTransform: 'none',
          px: 2,
          py: 0.75,
          borderRadius: 2
        }}
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" fontWeight="medium">
            {selectedDC.name}
          </Typography>
          {selectedDC.city && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1 }}>
              {selectedDC.city}
            </Typography>
          )}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: { minWidth: 250, mt: 1 }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Select Distribution Center
          </Typography>
        </Box>
        <Divider />

        {distributionCenters.map((dc) => (
          <MenuItem
            key={dc.id}
            onClick={() => handleSelect(dc)}
            selected={dc.id === selectedDC.id}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.50'
              }
            }}
          >
            <ListItemIcon>
              {dc.id === selectedDC.id ? (
                <CheckIcon color="primary" />
              ) : (
                <LocationOnIcon color="action" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={dc.name}
              secondary={dc.city ? `${dc.city}, ${dc.state}` : dc.code}
              primaryTypographyProps={{ fontWeight: dc.id === selectedDC.id ? 'medium' : 'normal' }}
            />
            {!dc.is_active && (
              <Chip label="Inactive" size="small" color="default" />
            )}
          </MenuItem>
        ))}

        {distributionCenters.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No distribution centers found" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
